<script setup>
import TvScorebug from '~/components/tv/TvScorebug.vue';
import {TV_COMPETITION_ICON, tvDayMonth} from '~/utils/tvDisplay';

// The bottom crawl: every recent result as a compact scorebug under its
// competition, like a sports channel's results bar. Matches being scored live
// lead, when there are any.
const props = defineProps({
  results: {type: Array, default: () => []},
  live: {type: Array, default: () => []},
  freshIds: {type: Object, default: () => new Set()},
});

const liveAsResult = (match) => {
  let root = null;
  try {
    root = match.state ? JSON.parse(match.state)?.path?.[0] : null;
  } catch (e) {
    root = null;
  }
  return {
    homeTeamName: match.homeTeamName,
    awayTeamName: match.awayTeamName,
    homeResult: root?.home ?? 0,
    awayResult: root?.away ?? 0,
  };
};

const hasItems = computed(() => props.results.length > 0 || props.live.length > 0);
</script>

<template>
  <footer class="tv-ticker">
    <div class="tv-ticker-label">
      <span class="tv-ticker-icon"><v-icon icon="mdi-scoreboard" size="30"/></span>
      <span class="tv-ticker-title">Results</span>
    </div>
    <div class="tv-ticker-track">
      <NuxtMarquee v-if="hasItems" :speed="60" :auto-fill="true">
        <div v-for="match in live" :key="'l' + match.sessionId" class="tv-tick">
          <div class="tv-tick-meta"><span class="tv-tick-live">Live</span>{{ match.competitionName }}</div>
          <tv-scorebug :result="liveAsResult(match)" size="ticker"/>
        </div>
        <div v-for="result in results" :key="result.fixtureId" class="tv-tick">
          <div class="tv-tick-meta">
            <span v-if="freshIds.has(result.fixtureId)" class="tv-tick-new">New</span>
            <v-icon :icon="TV_COMPETITION_ICON[result.competitionKind]" size="18"/>
            <span class="tv-tick-comp">{{ result.competitionName }}</span>
            <span class="tv-tick-date">{{ tvDayMonth(result.playedAt) }}</span>
          </div>
          <tv-scorebug :result="result" size="ticker"/>
        </div>
      </NuxtMarquee>
      <span v-else class="tv-ticker-empty">Results appear here as soon as they're entered</span>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.tv-ticker {
  flex-shrink: 0;
  /* Doesn't collapse to a sliver when there are no results yet */
  min-height: 110px;
  display: flex;
  align-items: stretch;
  background: var(--tv-panel);
  border-top: 1px solid var(--tv-line);
}

.tv-ticker-label {
  display: flex;
  align-items: stretch;
  gap: 18px;
  padding-right: 28px;
  background: var(--tv-bar);
  border-right: 1px solid var(--tv-line);
  position: relative;
  z-index: 1;
}

/* The same icon block as every section's header bar */
.tv-ticker-icon {
  width: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 10px;
  background: var(--tv-accent);
  color: var(--tv-on-accent);
  clip-path: polygon(0 0, 100% 0, calc(100% - 16px) 100%, 0 100%);
}

.tv-ticker-title {
  display: flex;
  align-items: center;
  font-family: var(--tv-display);
  font-weight: 800;
  font-size: 38px;
  color: var(--tv-ink);
}

.tv-ticker-track {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.tv-tick {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px 44px 28px;
  border-left: 1px solid var(--tv-line);
}

.tv-tick-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  color: var(--tv-muted);
  white-space: nowrap;
}

.tv-tick-comp {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* When it was played, at the end of the label line */
.tv-tick-date {
  margin-left: auto;
  padding-left: 16px;
  font-weight: 600;
  color: var(--tv-ink);
}

.tv-tick-new,
.tv-tick-live {
  padding: 0 8px;
  border-radius: 3px;
  font-weight: 600;
  background: var(--tv-accent);
  color: var(--tv-on-accent);
}

.tv-tick-live {
  background: #e0243c;
  color: #fff;
}

.tv-ticker-empty {
  padding: 0 44px;
  font-size: 24px;
  color: var(--tv-muted);
}
</style>
